import { useQueries, useQuery } from '@tanstack/react-query'
import { getWoodworkIndex } from '../api/getWoodworkIndex'
import { getWoodworkProjectManifest } from '../api/getWoodworkProjectManifest'
import { mediaUrl } from '../config/media'
import { projectDateSortKey } from '../content/formatProjectDate'
import { normalizeMaterials } from '../content/woodworkMaterials'
import type { WoodworkManifestProject } from '../content/woodworkManifest'
import { woodworkProjects as fallbackProjects } from '../content/site'

export type WoodworkGalleryImage = {
  readonly src: string
  readonly alt: string
  readonly caption?: string
}

export type WoodworkProjectView = {
  readonly id: string
  readonly title: string
  readonly description: string
  readonly status: WoodworkManifestProject['status']
  readonly startDate?: string
  readonly endDate?: string
  readonly materials: readonly string[]
  readonly imageSrc?: string
  readonly imageAlt?: string
  readonly images: readonly WoodworkGalleryImage[]
  readonly hasGallery: boolean
}

const statusLabel: Record<WoodworkManifestProject['status'], string> = {
  planned: 'Coming soon',
  'in-progress': 'In progress',
  complete: 'Complete'
}

const projectStaleTime = 5 * 60 * 1000

function buildGalleryImages(project: WoodworkManifestProject): WoodworkGalleryImage[] {
  const seen = new Set<string>()
  const images: WoodworkGalleryImage[] = []

  const add = (path: string, alt: string, caption?: string) => {
    const src = mediaUrl(path)
    if (seen.has(src)) return
    seen.add(src)
    images.push({ src, alt, caption })
  }

  if (project.hero) {
    add(project.hero, project.heroAlt ?? project.title)
  }

  for (const image of project.images ?? []) {
    add(image.src, image.alt ?? project.title, image.caption)
  }

  return images
}

function toView(project: WoodworkManifestProject): WoodworkProjectView {
  const images = buildGalleryImages(project)
  const hero = images[0]

  return {
    id: project.id,
    title: project.title,
    description: project.description,
    status: project.status,
    startDate: project.startDate,
    endDate: project.endDate,
    materials: normalizeMaterials(project.materials),
    imageSrc: hero?.src,
    imageAlt: hero?.alt ?? project.title,
    images,
    hasGallery: images.length > 0
  }
}

function fallbackViews(): WoodworkProjectView[] {
  return fallbackProjects.map((p) => ({
    id: p.id,
    title: p.title,
    description: p.description,
    status: p.status,
    materials: normalizeMaterials(p.materials),
    images: [],
    hasGallery: false
  }))
}

function sortProjects(views: WoodworkProjectView[]): WoodworkProjectView[] {
  return [...views].sort(
    (a, b) =>
      projectDateSortKey(b.startDate, b.endDate) -
        projectDateSortKey(a.startDate, a.endDate) ||
      a.title.localeCompare(b.title)
  )
}

function useWoodworkProjectQueries(projectIds: readonly string[], enabled: boolean) {
  return useQueries({
    queries: projectIds.map((id) => ({
      queryKey: ['woodwork-project', id] as const,
      queryFn: () => getWoodworkProjectManifest(id),
      enabled,
      staleTime: projectStaleTime,
      retry: 1
    }))
  })
}

export function useWoodworkProjects() {
  const indexQuery = useQuery({
    queryKey: ['woodwork-index'],
    queryFn: getWoodworkIndex,
    staleTime: projectStaleTime,
    retry: 1
  })

  const catalog = indexQuery.data
  const usePerProjectFiles = Boolean(catalog && !catalog.inlinedProjects)
  const projectIds = usePerProjectFiles ? (catalog?.projectIds ?? []) : []

  const projectQueries = useWoodworkProjectQueries(projectIds, usePerProjectFiles)

  const manifestProjects = sortProjects(
    catalog?.inlinedProjects
      ? catalog.inlinedProjects.filter((p) => !p.hidden).map(toView)
      : projectQueries
          .map((q) => q.data)
          .filter((p): p is WoodworkManifestProject => p !== undefined && !p.hidden)
          .map(toView)
  )

  const awaitingPerProject =
    usePerProjectFiles && projectIds.length > 0 && projectQueries.some((q) => q.isLoading)

  const stillLoading = indexQuery.isLoading || awaitingPerProject

  const projects = stillLoading
    ? []
    : manifestProjects.length > 0
      ? manifestProjects
      : indexQuery.isError
        ? fallbackViews()
        : []

  const indexError = indexQuery.error
  const projectError = projectQueries.find((q) => q.error)?.error

  return {
    projects,
    statusLabel,
    isLoading: stillLoading,
    isManifest: manifestProjects.length > 0,
    error: indexError ?? projectError
  }
}

export function useWoodworkProject(projectId: string | undefined) {
  const indexQuery = useQuery({
    queryKey: ['woodwork-index'],
    queryFn: getWoodworkIndex,
    staleTime: projectStaleTime,
    retry: 1
  })

  const catalog = indexQuery.data
  const inlined = catalog?.inlinedProjects?.find((p) => p.id === projectId)

  const projectQuery = useQuery({
    queryKey: ['woodwork-project', projectId] as const,
    queryFn: () => getWoodworkProjectManifest(projectId!),
    enabled: Boolean(projectId) && !inlined,
    staleTime: projectStaleTime,
    retry: 1
  })

  const raw = inlined ?? projectQuery.data
  const project = raw && !raw.hidden ? toView(raw) : undefined

  const isLoading =
    indexQuery.isLoading ||
    (Boolean(projectId) && !inlined && projectQuery.isLoading)

  const inIndex = projectId ? catalog?.projectIds.includes(projectId) : false
  const notFound =
    !isLoading &&
    projectId !== undefined &&
    indexQuery.isSuccess &&
    (!inIndex || projectQuery.isError || raw?.hidden === true)

  return {
    project,
    statusLabel,
    isLoading,
    isManifest: Boolean(raw),
    notFound
  }
}

export function projectCardDescription(project: WoodworkProjectView): string {
  const text = project.description
  return text.length > 160 ? `${text.slice(0, 157)}…` : text
}

export function woodworkProjectPath(projectId: string): string {
  return `/work/${projectId}`
}
